let scriptLoadingPromise: Promise<void> | null = null;

const isV3ScriptLoaded = (): boolean => {
  return !!document.querySelector(`script[src*="recaptcha/api.js?render="]`);
};

const GOOGLE_RECAPTCHA_SCRIPT = "https://www.google.com/recaptcha/api.js";

const getDirectScriptUrl = (siteKey: string): string =>
  `${GOOGLE_RECAPTCHA_SCRIPT}?render=${encodeURIComponent(siteKey)}`;

const loadScriptFromUrl = (url: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.onload = () => {
      if (typeof window.grecaptcha?.ready === "function") {
        window.grecaptcha.ready(() => resolve());
      } else {
        resolve();
      }
    };
    script.onerror = () => {
      script.remove();
      reject(new Error("Failed to load reCAPTCHA v3 script"));
    };
    document.head.appendChild(script);
  });

export const loadRecaptchaV3Script = (siteKey: string): Promise<void> => {
  if (!siteKey || siteKey.length < 10) {
    return Promise.reject(new Error("reCAPTCHA site key is missing or invalid"));
  }
  if (isV3ScriptLoaded()) {
    return typeof window.grecaptcha?.ready === "function"
      ? new Promise((resolve) => window.grecaptcha.ready(() => resolve()))
      : Promise.resolve();
  }

  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  scriptLoadingPromise = loadScriptFromUrl(getDirectScriptUrl(siteKey)).catch((err) => {
    scriptLoadingPromise = null;
    return Promise.reject(err);
  });

  return scriptLoadingPromise;
};

/** Reset so a subsequent loadRecaptchaV3Script() will try again (for example after user clicks Retry). */
export const resetRecaptchaLoad = (): void => {
  scriptLoadingPromise = null;
};

export const executeRecaptchaV3 = async (siteKey: string, action: string): Promise<string> => {
  await loadRecaptchaV3Script(siteKey);
  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(siteKey, { action }).then(resolve).catch(reject);
    });
  });
};
